// src/screens/SharedPlay.tsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Stage, Layer, Image as KonvaImage, Circle, Text, Group,
} from "react-konva";
import useImage from "use-image";
import { ChevronLeft, ChevronRight, LoaderCircle } from "lucide-react";
import { DefenderGlyph } from "../components/DefenderGlyph";
import PlayArrow from "../components/PlayArrow";
import i18n from "../i18n";

/* ===== i18n mini-hook (safe) ===== */
function useI18nSafe() {
  const [, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t };
}

export const SharedPlay = () => {
  const { t } = useI18nSafe();
  const { id } = useParams();
  const [play, setPlay] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [image] = useImage("/court.png");
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [showAllPhases, setShowAllPhases] = useState(false);
  const [stageWidth, setStageWidth] = useState(0);
  const [stageHeight, setStageHeight] = useState(0);

  // ---------------- mapping coordonnées ----------------
  // Les plays récents sont stockés en coordonnées normalisées (0..1) ; les anciens en pixels
  // dans une résolution de référence. On ramène tout vers la taille réelle du Stage.
  const phaseIsNormalized = (phase: any) => {
    let maxX = 0, maxY = 0;
    (phase?.players ?? []).forEach((p: any) => { if (!p) return; maxX = Math.max(maxX, +p.x || 0); maxY = Math.max(maxY, +p.y || 0); });
    (phase?.defenders ?? []).forEach((d: any) => { if (!d) return; maxX = Math.max(maxX, +d.x || 0); maxY = Math.max(maxY, +d.y || 0); });
    (phase?.arrows ?? []).forEach((a: any) => {
      [a?.from, a?.to, a?.control].filter(Boolean).forEach((v: any) => {
        maxX = Math.max(maxX, +v.x || 0); maxY = Math.max(maxY, +v.y || 0);
      });
    });
    return maxX <= 1.01 && maxY <= 1.01;
  };

  const getPixelBase = () => {
    const m = play?.meta || play?.data_meta || {};
    const bw = m.stageW ?? m.baseW ?? m.w ?? play?.canvasSize?.w;
    const bh = m.stageH ?? m.baseH ?? m.h ?? play?.canvasSize?.h;
    if (bw && bh) return { bw: Number(bw), bh: Number(bh) };
    const natW = image?.naturalWidth || stageWidth || 1;
    const natH = image?.naturalHeight || stageHeight || 1;
    return { bw: natW, bh: natH };
  };

  const toCanvasPt = (phase: any, pt: any) => {
    if (!pt) return pt;
    if (phaseIsNormalized(phase)) {
      return { x: pt.x * stageWidth, y: pt.y * stageHeight };
    }
    const { bw, bh } = getPixelBase();
    return { x: (pt.x / bw) * stageWidth, y: (pt.y / bh) * stageHeight };
  };

  useEffect(() => {
    if (!image) return;
    const handleResize = () => {
      const naturalWidth = image.naturalWidth;
      const naturalHeight = image.naturalHeight;
      const availableWidth = Math.min(window.innerWidth * 0.9, 750);
      const scale = availableWidth / naturalWidth;
      setStageWidth(availableWidth);
      setStageHeight(naturalHeight * scale);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [image]);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from("plays")
        .select("*, user_profiles(full_name)")
        .eq("id", id)
        .eq("is_shared", true);

      if (error) {
        console.error("Erreur chargement play :", error);
        return;
      }
      if (data && data.length > 0) {
        setPlay(data[0]);
        setUser(data[0].user_profiles);
      }
    };
    load();
  }, [id]);

  if (!play || !image) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        <LoaderCircle className="animate-spin mr-2" />
        {t("sharedplay:loading")}
      </div>
    );
  }

  const phases = play.data || [];
  if (phases.length === 0) {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        {t("sharedplay:empty")}
      </div>
    );
  }

  const phasesToDisplay = showAllPhases ? phases : [phases[currentPhaseIndex]];

  return (
    <div className="min-h-screen p-4 max-w-3xl mx-auto bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <h1 className="text-xl font-bold mb-1 text-orange-600 dark:text-orange-400">{play.name}</h1>
      {user && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("sharedplay:by_author", { name: user.full_name })}
        </p>
      )}

      {/* Controls */}
      <div className="mb-4 flex justify-center items-center gap-4">
        {!showAllPhases && (
          <>
            <button
              onClick={() => setCurrentPhaseIndex((p) => Math.max(p - 1, 0))}
              disabled={currentPhaseIndex === 0}
              className="p-2 rounded bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50
                         hover:bg-gray-300 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
              aria-label={t("sharedplay:controls.prev")}
            >
              <ChevronLeft />
            </button>
            <span className="text-sm">
              {t("sharedplay:phase_counter", { current: currentPhaseIndex + 1, total: phases.length })}
            </span>
            <button
              onClick={() => setCurrentPhaseIndex((p) => Math.min(p + 1, phases.length - 1))}
              disabled={currentPhaseIndex === phases.length - 1}
              className="p-2 rounded bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50
                         hover:bg-gray-300 dark:hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
              aria-label={t("sharedplay:controls.next")}
            >
              <ChevronRight />
            </button>
          </>
        )}
        <button
          onClick={() => setShowAllPhases((v) => !v)}
          className="ml-4 px-3 py-1 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-sm
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
        >
          {showAllPhases ? t("sharedplay:controls.view_by_phase") : t("sharedplay:controls.view_global")}
        </button>
      </div>

      {/* Terrain + dessin */}
      {phasesToDisplay.map((phase: any, phaseIndex: number) => (
        <div
          key={phaseIndex}
          className="flex justify-center mb-8 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-3"
        >
          <Stage width={stageWidth} height={stageHeight}>
            <Layer>
              <KonvaImage image={image} width={stageWidth} height={stageHeight} />

              {/* Défenseurs */}
              {Array.isArray(phase.defenders) &&
                phase.defenders.map((d: any, i: number) => {
                  const D = toCanvasPt(phase, d);
                  return <DefenderGlyph key={`def-${i}`} x={D.x} y={D.y} angle={d.angle ?? 0} />;
                })}

              {/* Flèches / chemins */}
              {Array.isArray(phase.arrows) &&
                phase.arrows.map((arrow: any, i: number) => {
                  const fromRaw = arrow.fromPlayerIndex !== undefined ? phase.players[arrow.fromPlayerIndex] : arrow.from;
                  const toRaw = arrow.toPlayerIndex !== undefined ? phase.players[arrow.toPlayerIndex] : arrow.to;
                  if (!fromRaw || !toRaw) return null;

                  return (
                    <PlayArrow
                      key={`arrow-${i}`}
                      type={arrow.type}
                      from={toCanvasPt(phase, fromRaw)}
                      to={toCanvasPt(phase, toRaw)}
                      control={arrow.control ? toCanvasPt(phase, arrow.control) : null}
                      fromIsPlayer={arrow.fromPlayerIndex !== undefined}
                      toIsPlayer={arrow.toPlayerIndex !== undefined}
                    />
                  );
                })}

              {/* Joueurs */}
              {phase.players.map((p: any, i: number) => {
                const P = toCanvasPt(phase, p);
                return (
                <Group key={i} x={P.x} y={P.y}>
                  {p.hasBall ? (
                    <>
                      <Circle radius={15} stroke="black" strokeWidth={2} />
                      <Text
                        text={p.number.toString()}
                        fontSize={16}
                        fill="black"
                        align="center"
                        verticalAlign="middle"
                        width={30}
                        height={30}
                        offsetX={15}
                        offsetY={15}
                      />
                    </>
                  ) : (
                    <Text
                      text={p.number.toString()}
                      fontSize={16}
                      fill="black"
                      align="center"
                      verticalAlign="middle"
                      width={30}
                      height={30}
                      offsetX={15}
                      offsetY={15}
                    />
                  )}
                </Group>
                );
              })}
            </Layer>
          </Stage>
        </div>
      ))}
    </div>
  );
};
